import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  NAPTÁRTÍPUSOK (program, szünet, ünnepély…) – törzsadat.
 *
 *  A típus neve, színe és ikonja eddig kódban élt, új típust felvenni csak kiadással
 *  lehetett. Itt szerkeszthető: a naptár, az órarend eseménysávja, a beágyazott nézetek
 *  és a naptár-feliratkozás is innen veszi.
 *
 *  A típus KULCSA köti az eseményt a típushoz, ezért nem változik (nem is látszik).
 *  Törölni csak a még nem használt típust lehet; a használtat elrejteni: új eseményhez
 *  nem választható, a meglévő események megjelenése nem változik.
 */
@Component({
  selector: 'app-admin-naptar-tipus',
  templateUrl: './naptar-tipus.component.html',
  styleUrls: ['./naptar-tipus.component.scss'],
})
export class AdminNaptarTipusComponent implements OnInit {
  sajatIntezmeny: any;
  tipusok: any[] = [];
  toltodik = false;
  hiba = '';
  uj: any = { nev: '', szin: '#3880ff', ikon: 'calendar' };
  ujNyitva = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.betolt();
  }

  get intezmeny(): string {
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/naptar-tipusok/${this.intezmeny}?hasznalat=1`));
      this.tipusok = (Array.isArray(r) ? r : []).map((t: any) => ({ ...t, eredeti: { ...t } }));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A típusok nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  /** Az ion-icon csak érvényes névvel rajzol – a beírás közben ne tűnjön el az előnézet. */
  ikonElonezet(ikon: string): string {
    return /^[a-z0-9-]+$/.test(String(ikon || '')) ? ikon : 'help-circle-outline';
  }

  modosult(t: any): boolean {
    const e = t.eredeti || {};
    return t.nev !== e.nev || t.szin !== e.szin || (t.ikon || '') !== (e.ikon || '')
      || Number(t.sorrend) !== Number(e.sorrend);
  }

  private hibaUzenet(e: any, alap: string) { return e?.error?.error || alap; }

  async ment(t: any) {
    if (!String(t.nev || '').trim()) { this.data.sendToast('Adj nevet a típusnak.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.put(`/api/naptar-tipusok/${t.id}/${this.intezmeny}`,
        { nev: t.nev, szin: t.szin, ikon: t.ikon, sorrend: t.sorrend, rejtett: t.rejtett }));
      this.data.sendToast('Mentve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A mentés nem sikerült.'), 'error');
    }
  }

  async rejtes(t: any) {
    const eredetiNelkul = { ...t.eredeti };
    try {
      await firstValueFrom(this.http.put(`/api/naptar-tipusok/${t.id}/${this.intezmeny}`,
        { nev: eredetiNelkul.nev, szin: eredetiNelkul.szin, ikon: eredetiNelkul.ikon,
          sorrend: eredetiNelkul.sorrend, rejtett: !t.rejtett }));
      this.data.sendToast(t.rejtett
        ? `„${t.nev}" újra választható.`
        : `„${t.nev}" elrejtve – új eseményhez nem választható, a meglévők maradnak.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A módosítás nem sikerült.'), 'error');
    }
  }

  torlesCim(t: any): string {
    return t.hasznalat
      ? `${t.hasznalat} esemény használja – nem törölhető, de elrejthető`
      : 'Törlés';
  }

  async torol(t: any) {
    if (!confirm(`Törlöd a(z) „${t.nev}" típust?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/naptar-tipusok/${t.id}/${this.intezmeny}`));
      this.data.sendToast('Törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A törlés nem sikerült.'), 'error');
    }
  }

  async ujMent() {
    const nev = String(this.uj.nev || '').trim();
    if (!nev) { this.data.sendToast('Adj nevet a típusnak.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.post(`/api/naptar-tipusok/${this.intezmeny}`, { ...this.uj, nev }));
      this.data.sendToast('Típus felvéve – mostantól választható a naptárban.', 'success');
      this.uj = { nev: '', szin: '#3880ff', ikon: 'calendar' };
      this.ujNyitva = false;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(this.hibaUzenet(e, 'A felvétel nem sikerült.'), 'error');
    }
  }
}
